import {
  FloatingPortal,
  autoUpdate,
  flip,
  offset,
  useClick,
  useDismiss,
  useFloating,
  useInteractions,
} from '@floating-ui/react'
import { Switch } from 'arp/design-system/component/Switch/Switch'
import { ReactNode, useState } from 'react'
import { Link } from 'react-router'
import { cn } from '@/lib/utils'
import './menu-dropdown.css'

export interface MenuDropdownItem {
  label: string
  icon?: ReactNode
  onClick?: () => void
  href?: string
  /** Open link in new tab (only applies when href is set) */
  external?: boolean
  disabled?: boolean
  separator?: boolean
  /** Style as destructive action (red text, red hover background) */
  destructive?: boolean
  /** Custom className to apply to the item */
  className?: string
  /** Render the item as an on/off switch; toggling keeps the menu open */
  toggle?: { isChecked: boolean; onChange: (isChecked: boolean) => void }
}

interface MenuDropdownProps {
  trigger: ReactNode
  items: MenuDropdownItem[]
  /** Optional title displayed at top of menu */
  title?: string
  /** Optional subtitle displayed below title */
  subtitle?: string
  align?: 'start' | 'end'
  /** Position menu to the side of trigger instead of below. Can be true/'right' for right side, or 'top' for above */
  side?: boolean | 'right' | 'top'
  /** Offset from the trigger */
  sideOffset?: number
  className?: string
  menuClassName?: string
}

/**
 * A simple menu dropdown component for triggering actions.
 * Styled consistently with the Dropdown selection component.
 */
export const MenuDropdown = ({
  trigger,
  items,
  title,
  subtitle,
  align = 'start',
  side = false,
  sideOffset = 8,
  className,
  menuClassName,
}: MenuDropdownProps) => {
  const [isOpen, setIsOpen] = useState(false)

  const getPlacement = ():
    | 'right-start'
    | 'right-end'
    | 'left-start'
    | 'left-end'
    | 'top-start'
    | 'top-end'
    | 'bottom-start'
    | 'bottom-end' => {
    if (side === 'top') {
      return align === 'end' ? 'top-end' : 'top-start'
    }
    if (side === true || side === 'right') {
      // align=end means bottom of menu aligns with bottom of trigger
      return align === 'end' ? 'right-end' : 'right-start'
    }
    return align === 'end' ? 'bottom-end' : 'bottom-start'
  }

  const getFallbackPlacements = (): Array<
    'left-start' | 'left-end' | 'top-start' | 'top-end' | 'bottom-start' | 'bottom-end'
  > => {
    if (side === 'top') {
      return align === 'end' ? ['bottom-end'] : ['bottom-start']
    }
    if (side === true || side === 'right') {
      return align === 'end' ? ['left-end'] : ['left-start']
    }
    return ['top-start', 'top-end']
  }

  const { refs, floatingStyles, context } = useFloating({
    open: isOpen,
    onOpenChange: setIsOpen,
    strategy: 'fixed',
    placement: getPlacement(),
    whileElementsMounted: autoUpdate,
    middleware: [
      offset(side ? { mainAxis: sideOffset, crossAxis: 0 } : 4),
      flip({
        fallbackPlacements: getFallbackPlacements(),
      }),
    ],
  })

  const click = useClick(context)
  const dismiss = useDismiss(context)

  const { getReferenceProps, getFloatingProps } = useInteractions([click, dismiss])

  return (
    <div className={cn('relative', className)}>
      <div ref={refs.setReference} {...getReferenceProps()}>
        {trigger}
      </div>

      {isOpen && (
        <FloatingPortal>
          <div
            ref={refs.setFloating}
            className={cn(
              'z-[9999] border border-gray-4 rounded-sm bg-white shadow-md outline-none animate-in fade-in-0 zoom-in-95 p-1',
              menuClassName,
            )}
            style={floatingStyles}
            {...getFloatingProps()}
          >
            {(title || subtitle) && (
              <div className="mb-2 border-b border-gray-4 p-2">
                {title && <div className="text-xs font-medium text-gray-12">{title}</div>}
                {subtitle && <div className="text-xs text-gray-10">{subtitle}</div>}
              </div>
            )}
            {items.map((item, index) => {
              if (item.separator) {
                return <div key={index} className="my-2 h-px bg-gray-4" />
              }

              const itemContent = (
                <>
                  {item.icon && (
                    <span className="flex size-4 items-center justify-center">{item.icon}</span>
                  )}
                  <span>{item.label}</span>
                </>
              )

              const itemClassName = cn(
                'flex cursor-pointer items-center gap-2 rounded-sm px-4 py-2 text-xs',
                item.destructive
                  ? 'text-red-10 hover:bg-red-3 hover:text-red-10'
                  : 'text-gray-10 hover:bg-gray-2 hover:text-gray-12',
                item.disabled && 'cursor-not-allowed opacity-50',
                item.className,
              )

              if (item.toggle) {
                return (
                  <label key={index} className={cn(itemClassName, 'menu-dropdown-toggle')}>
                    {item.icon && (
                      <span className="flex size-4 items-center justify-center">{item.icon}</span>
                    )}
                    <span className="label">{item.label}</span>
                    <span className="arp-root switch">
                      <Switch isChecked={item.toggle.isChecked} onChange={item.toggle.onChange} />
                    </span>
                  </label>
                )
              }

              if (item.href) {
                if (item.external) {
                  return (
                    <a
                      key={index}
                      className={itemClassName}
                      href={item.href}
                      onClick={() => {
                        if (!item.disabled) {
                          item.onClick?.()
                          setIsOpen(false)
                        }
                      }}
                      rel="noopener noreferrer"
                      target="_blank"
                    >
                      {itemContent}
                    </a>
                  )
                }
                return (
                  <Link
                    key={index}
                    className={itemClassName}
                    to={item.href}
                    onClick={() => {
                      if (!item.disabled) {
                        item.onClick?.()
                        setIsOpen(false)
                      }
                    }}
                  >
                    {itemContent}
                  </Link>
                )
              }

              return (
                <div
                  key={index}
                  className={itemClassName}
                  onClick={() => {
                    if (!item.disabled) {
                      item.onClick?.()
                      setIsOpen(false)
                    }
                  }}
                >
                  {itemContent}
                </div>
              )
            })}
          </div>
        </FloatingPortal>
      )}
    </div>
  )
}
